class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const requestId = ++FacetFiltersForm.requestCounter;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        // A newer filter change was made while this request was in flight: keep the response cached but
        // don't paint it, otherwise a slow older response (eg the empty state) overwrites the current one.
        if (requestId !== FacetFiltersForm.requestCounter) return;
        FacetFiltersForm.renderResponse(html, event, true);
      })
      .catch((error) => {
        if (requestId !== FacetFiltersForm.requestCounter) return;
        console.error('Facet filter request failed', error);
        FacetFiltersForm.clearLoading();
      });
  }

  // Paints the product grid first and then each other part on its own, so an error while updating the
  // sidebar can never leave the grid stuck in its loading state with stale products.
  static renderResponse(html, event, includeSort) {
    const steps = [
      () => FacetFiltersForm.renderProductGridContainer(html),
      () => FacetFiltersForm.renderFilters(html, event),
      () => FacetFiltersForm.renderProductCount(html),
    ];
    if (includeSort) {
      steps.push(() => FacetFiltersForm.renderDesktopSort(html), () => FacetFiltersForm.renderMobileSortDrawer(html));
    }
    steps.push(() => {
      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    });
    steps.push(() =>
      FacetFiltersForm.syncMobileSortSelection(new URLSearchParams(FacetFiltersForm.searchParamsPrev).get('sort_by'))
    );
    steps.forEach((step) => {
      try {
        step();
      } catch (error) {
        console.error('Facet filter render step failed', error);
      }
    });
    FacetFiltersForm.clearLoading();
  }

  static clearLoading() {
    document.querySelectorAll('#ProductGridContainer .collection.loading, #ProductCount.loading, #ProductCountDesktop.loading').forEach((element) => {
      element.classList.remove('loading');
    });
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderResponse(html, event, false);
  }

  static renderProductGridContainer(html) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    const newContainer = newDocument.getElementById('ProductGridContainer');
    const currentContainer = document.getElementById('ProductGridContainer');
    
    // Preserve the subcategory carousel and filter/sort buttons before updating
    const subcategoryCarousel = currentContainer.querySelector('.subcategory-carousel-wrapper');
    const mobileFilterButtons = currentContainer.querySelector('.mobile-facets-wrapper');
    const mobileProductCount = currentContainer.querySelector('.mobile-product-count');
    const mobileActiveFilters = currentContainer.querySelector('.mobile-active-facets');
    
    // Only remove the new carousel if we have a preserved one to replace it with
    // This prevents losing the carousel when returning from empty state
    if (subcategoryCarousel) {
      const newCarousel = newContainer.querySelector('.subcategory-carousel-wrapper');
      if (newCarousel) {
        newCarousel.remove();
      }
    }
    
    // Update the container
    currentContainer.innerHTML = newContainer.innerHTML;
    
    // Re-insert the preserved subcategory carousel if it existed
    if (subcategoryCarousel) {
      const collectionBanner = currentContainer.querySelector('.collection-hero__text-wrapper');
      if (collectionBanner) {
        collectionBanner.appendChild(subcategoryCarousel);
        
        // Re-initialize Splide carousel if it exists
        const splideElement = subcategoryCarousel.querySelector('.subcategory-splide');
        if (splideElement && typeof Splide !== 'undefined') {
          const itemCount = parseInt(splideElement.getAttribute('data-count')) || 0;
          
          const splide = new Splide('.subcategory-splide', {
            type: 'slide',
            perPage: 5,
            perMove: 1,
            gap: '20px',
            pagination: false,
            arrows: true,
            rewind: false,
            breakpoints: {
              1200: { perPage: 4 },
              991: { perPage: 3 },
              767: { perPage: 2, gap: '15px' },
              480: { perPage: 2, gap: '10px' }
            }
          });
          
          splide.mount();
          
          // Update arrow visibility
          function updateArrowVisibility() {
            const prevArrow = splideElement.querySelector('.splide__arrow--prev');
            const nextArrow = splideElement.querySelector('.splide__arrow--next');
            
            if (!prevArrow || !nextArrow) return;
            
            const currentIndex = splide.index;
            const totalSlides = splide.length;
            const perPage = splide.options.perPage;
            
            if (totalSlides <= perPage) {
              prevArrow.style.display = 'none';
              nextArrow.style.display = 'none';
              return;
            }
            
            if (currentIndex === 0) {
              prevArrow.style.display = 'none';
              nextArrow.style.display = 'flex';
            } else if (currentIndex >= totalSlides - perPage) {
              prevArrow.style.display = 'flex';
              nextArrow.style.display = 'none';
            } else {
              prevArrow.style.display = 'none';
              nextArrow.style.display = 'flex';
            }
          }
          
          updateArrowVisibility();
          splide.on('moved', updateArrowVisibility);
        }
      }
    } else {
      // If no carousel was preserved, initialize any new carousel from the server
      const newSplideElement = currentContainer.querySelector('.subcategory-splide');
      if (newSplideElement && typeof Splide !== 'undefined') {
        const splide = new Splide('.subcategory-splide', {
          type: 'slide',
          perPage: 5,
          perMove: 1,
          gap: '20px',
          pagination: false,
          arrows: true,
          rewind: false,
          breakpoints: {
            1200: { perPage: 4 },
            991: { perPage: 3 },
            767: { perPage: 2, gap: '15px' },
            480: { perPage: 2, gap: '10px' }
          }
        });
        
        splide.mount();
        
        function updateArrowVisibility() {
          const prevArrow = newSplideElement.querySelector('.splide__arrow--prev');
          const nextArrow = newSplideElement.querySelector('.splide__arrow--next');
          
          if (!prevArrow || !nextArrow) return;
          
          const currentIndex = splide.index;
          const totalSlides = splide.length;
          const perPage = splide.options.perPage;
          
          if (totalSlides <= perPage) {
            prevArrow.style.display = 'none';
            nextArrow.style.display = 'none';
            return;
          }
          
          if (currentIndex === 0) {
            prevArrow.style.display = 'none';
            nextArrow.style.display = 'flex';
          } else if (currentIndex >= totalSlides - perPage) {
            prevArrow.style.display = 'flex';
            nextArrow.style.display = 'none';
          } else {
            prevArrow.style.display = 'none';
            nextArrow.style.display = 'flex';
          }
        }
        
        updateArrowVisibility();
        splide.on('moved', updateArrowVisibility);
      }
    }

    // Re-insert preserved mobile filter/sort buttons and update product count/filters from server
    const collectionDiv = currentContainer.querySelector('.collection');
    if (collectionDiv) {
      // Get new product count and active filters from server response
      const newMobileProductCount = currentContainer.querySelector('.mobile-product-count');
      const newMobileActiveFilters = currentContainer.querySelector('.mobile-active-facets');
      
      // Remove new filter buttons from server (we preserve the old ones with event listeners)
      const newMobileFilterButtons = currentContainer.querySelector('.mobile-facets-wrapper');
      if (newMobileFilterButtons) newMobileFilterButtons.remove();
      
      // Re-insert preserved filter/sort buttons at the beginning
      if (mobileFilterButtons) {
        collectionDiv.insertBefore(mobileFilterButtons, collectionDiv.firstChild);
        
        // Place new product count after buttons
        if (newMobileProductCount) {
          mobileFilterButtons.after(newMobileProductCount);
          
          // Place new active filters after product count
          if (newMobileActiveFilters) {
            newMobileProductCount.after(newMobileActiveFilters);
          }
        }
      } else {
        // If no preserved buttons, just ensure new elements are in correct order
        if (newMobileProductCount) {
          collectionDiv.insertBefore(newMobileProductCount, collectionDiv.firstChild);
        }
        if (newMobileActiveFilters && newMobileProductCount) {
          newMobileProductCount.after(newMobileActiveFilters);
        }
      }
      
      // Re-attach event listeners to preserved buttons
      FacetFiltersForm.attachMobileButtonListeners();
    }

    currentContainer
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }
  
  static attachMobileButtonListeners() {
    const filterButton = document.querySelector('[data-mobile-filter-trigger]');
    const sortButton = document.querySelector('[data-mobile-sort-trigger]');
    const filterDrawer = document.querySelector('.mobile-facets__disclosure');
    const sortDrawer = document.querySelector('.mobile-sort__disclosure');
    
    // Attach filter button listener
    if (filterButton && filterDrawer) {
      const newFilterButton = filterButton.cloneNode(true);
      filterButton.parentNode.replaceChild(newFilterButton, filterButton);
      
      newFilterButton.addEventListener('click', function() {
        const summary = filterDrawer.querySelector('summary');
        if (summary) {
          summary.click();
        }
      });
    }
    
    // Attach sort button listener
    if (sortButton && sortDrawer) {
      const newSortButton = sortButton.cloneNode(true);
      sortButton.parentNode.replaceChild(newSortButton, sortButton);
      
      newSortButton.addEventListener('click', function() {
        const summary = sortDrawer.querySelector('summary');
        if (summary) {
          summary.click();
        }
      });
    }
  }

  static renderProductCount(html) {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const newCountElement = parsedDocument.getElementById('ProductCount');
    if (!newCountElement) return;
    const count = newCountElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    const newMobileSeeResultsCount = parsedDocument.getElementById('MobileSeeResultsCount');
    const currentMobileSeeResultsCount = document.getElementById('MobileSeeResultsCount');
    if (newMobileSeeResultsCount && currentMobileSeeResultsCount) {
      currentMobileSeeResultsCount.textContent = newMobileSeeResultsCount.textContent;
    }
  }

  static renderDesktopSort(html) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    
    // Update the custom sort dropdown options
    const newSortPanel = newDocument.querySelector('#CustomSortPanel .custom-sort-panel__options');
    const currentSortPanel = document.querySelector('#CustomSortPanel .custom-sort-panel__options');
    
    if (newSortPanel && currentSortPanel) {
      currentSortPanel.innerHTML = newSortPanel.innerHTML;
      
      // Reinitialize the CustomSortDropdown to restore button click functionality
      if (typeof CustomSortDropdown !== 'undefined') {
        new CustomSortDropdown();
      }
    }
  }

  // Keeps the radios and ticks of the mobile sort drawer in line with the sort that is applied.
  // The drawer is only re-rendered from fresh responses, so this also covers cached responses and fast clicks.
  static syncMobileSortSelection(sortValue) {
    if (!sortValue) return;
    document.querySelectorAll('#FacetsWrapperMobileSort .mobile-facets__radio').forEach((radio) => {
      const isSelected = radio.value === sortValue;
      radio.checked = isSelected;
      const label = radio.closest('.mobile-facets__label');
      if (label) label.classList.toggle('mobile-facets__label--active', isSelected);
    });
  }

  static renderMobileSortDrawer(html) {
    const parser = new DOMParser();
    const newDocument = parser.parseFromString(html, 'text/html');
    
    // Update mobile sort drawer options (now located in FacetsWrapperMobileSort)
    const newMobileSortList = newDocument.querySelector('#FacetsWrapperMobileSort .mobile-facets__list');
    const currentMobileSortList = document.querySelector('#FacetsWrapperMobileSort .mobile-facets__list');
    
    if (newMobileSortList && currentMobileSortList) {
      currentMobileSortList.innerHTML = newMobileSortList.innerHTML;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop', '.active-facets-inline-wrapper'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = activeFacetsElement.innerHTML;
        
        // Show/hide the active-facets-inline container based on whether there are active filters
        if (selector === '.active-facets-inline-wrapper') {
          const activeFiltersContainer = targetElement.querySelector('.active-facets-inline');
          if (activeFiltersContainer) {
            const chipsContainer = activeFiltersContainer.querySelector('.active-facets-inline__chips');
            const hasChips = chipsContainer && chipsContainer.querySelectorAll('facet-remove').length > 0;
            if (hasChips) {
              activeFiltersContainer.style.display = '';
              activeFiltersContainer.removeAttribute('style');
            } else {
              activeFiltersContainer.style.display = 'none';
            }
          }
        }
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    // The mobile sort drawer submits itself (see the sort radio handler below). Without this guard the form's
    // `input` event also lands here 800ms later and re-sends the desktop sort form's old value, undoing the choice.
    if (event.target.closest('#FacetSortFormMobile')) return;
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.requestCounter = 0;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.textInputs = this.querySelectorAll('.price-range__input-text');
    this.sliders = this.querySelectorAll('.price-range__slider');
    this.sliderTrack = this.querySelector('.price-range__slider-track');
    this.sliderUpdateFrame = null;
    this.pendingSlider = null;
    
    this.textInputs.forEach((element) => {
      element.addEventListener('change', this.onTextInputChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
      element.addEventListener('input', this.onTextInput.bind(this));
    });
    
    this.sliders.forEach((slider) => {
      slider.addEventListener('input', this.onSliderInput.bind(this));
      slider.addEventListener('change', this.onSliderChange.bind(this));
    });
    
    this.setMinAndMaxValues();
    this.updateSliderTrack();
  }

  onTextInput(event) {
    const input = event.currentTarget;
    const type = input.getAttribute('data-type');
    
    // Clean the input value - remove all non-digits
    const cleanValue = input.value.replace(/[^\d]/g, '');
    
    if (cleanValue) {
      const value = Number(cleanValue);
      
      // Get max/min from data attributes and clean them (remove thousand separators)
      const dataMax = input.getAttribute('data-max');
      const dataMin = input.getAttribute('data-min');
      const max = dataMax ? Number(dataMax.replace(/[^\d]/g, '')) : Infinity;
      const min = dataMin ? Number(dataMin.replace(/[^\d]/g, '')) : 0;
      
      // For max input, prevent exceeding maximum
      // For min input, prevent exceeding current max value
      if (type === 'max' && value > max) {
        input.value = max.toLocaleString('id-ID');
        // Move cursor to end
        setTimeout(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
      } else if (type === 'min' && value > max) {
        input.value = max.toLocaleString('id-ID');
        setTimeout(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        }, 0);
      }
    }
  }

  onTextInputChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncTextToSlider(event.currentTarget);
    this.updateSliderTrack();
  }

  onSliderInput(event) {
    const slider = event.currentTarget;
    const type = slider.dataset.type;
    
    const minSlider = this.querySelector('.price-range__slider--min');
    const maxSlider = this.querySelector('.price-range__slider--max');
    
    // Add is-active class to the slider being moved
    minSlider.classList.remove('is-active');
    maxSlider.classList.remove('is-active');
    slider.classList.add('is-active');
    
    if (type === 'min') {
      if (parseInt(minSlider.value) > parseInt(maxSlider.value)) {
        minSlider.value = maxSlider.value;
      }
    } else {
      if (parseInt(maxSlider.value) < parseInt(minSlider.value)) {
        maxSlider.value = minSlider.value;
      }
    }
    
    this.updateSliderTrack();
    this.queueSliderSync(slider);
  }

  onSliderChange(event) {
    this.setMinAndMaxValues();
  }

  syncTextToSlider(textInput) {
    const type = textInput.dataset.type;
    const slider = this.querySelector(`.price-range__slider--${type}`);
    if (slider) {
      const cleanValue = textInput.value.replace(/[^\d.]/g, '');
      const valueInCents = cleanValue ? Math.round(parseFloat(cleanValue) * 100) : (type === 'min' ? 0 : slider.max);
      slider.value = valueInCents;
    }
  }

  syncSliderToText(slider) {
    const type = slider.dataset.type;
    const textInput = Array.from(this.textInputs).find(input => input.dataset.type === type);
    if (textInput) {
      const valueInDecimal = parseFloat(slider.value) / 100;
      const formatted = valueInDecimal.toLocaleString('id-ID', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).replace(/\.00$/, '');
      textInput.value = formatted;
    }
  }

  updateSliderTrack() {
    if (!this.sliderTrack) return;
    
    const minSlider = this.querySelector('.price-range__slider--min');
    const maxSlider = this.querySelector('.price-range__slider--max');
    
    if (!minSlider || !maxSlider) return;
    
    const min = parseInt(minSlider.value);
    const max = parseInt(maxSlider.value);
    const rangeMax = parseInt(minSlider.max);
    
    const minPercent = (min / rangeMax) * 100;
    const maxPercent = (max / rangeMax) * 100;
    
    this.sliderTrack.style.left = `${minPercent}%`;
    this.sliderTrack.style.width = `${maxPercent - minPercent}%`;
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) {
      event.preventDefault();
      return;
    }

    // Prevent multiple decimal points
    if (event.key === '.' && event.target.value.includes('.')) {
      event.preventDefault();
      return;
    }

    // For Indonesian Rupiah, prevent decimal input altogether (whole numbers only)
    if (event.key === '.') {
      event.preventDefault();
    }
  }

  setMinAndMaxValues() {
    const minInput = Array.from(this.textInputs).find(input => input.dataset.type === 'min');
    const maxInput = Array.from(this.textInputs).find(input => input.dataset.type === 'max');
    
    if (!minInput || !maxInput) return;
    
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', minInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    // Remove all non-numeric characters except the first decimal point
    let cleanValue = input.value.replace(/[^\d.]/g, '');
    
    // Remove multiple decimal points - keep only the first one
    const parts = cleanValue.split('.');
    if (parts.length > 2) {
      cleanValue = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // For Indonesian Rupiah, remove decimal points entirely (whole numbers only)
    cleanValue = cleanValue.replace(/\./g, '');
    
    // Convert to number
    const value = cleanValue ? Number(cleanValue) : 0;
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    // Validate against min/max bounds
    if (isNaN(value) || value < min) {
      input.value = min;
    } else if (value > max) {
      input.value = max;
    } else {
      input.value = value;
    }
  }

  queueSliderSync(slider) {
    this.pendingSlider = slider;
    if (this.sliderUpdateFrame) cancelAnimationFrame(this.sliderUpdateFrame);
    this.sliderUpdateFrame = requestAnimationFrame(() => {
      if (this.pendingSlider) {
        this.syncSliderToText(this.pendingSlider);
      }
      this.sliderUpdateFrame = null;
      this.pendingSlider = null;
    });
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Handle mobile sort radio button changes
document.addEventListener('change', function(event) {
  // Sort drawer radios are handled below, where the current filters and search term are kept
  if (event.target.classList.contains('mobile-facets__radio') && !event.target.closest('#FacetsWrapperMobileSort')) {
    const form = event.target.closest('form');
    if (form) {
      const facetForm = form.closest('facet-filters-form');
      if (facetForm) {
        const searchParams = facetForm.createSearchParams(form);
        facetForm.onSubmitForm(searchParams, event);
      }
    }
  }
});

// Handle mobile filter and sort button clicks
document.addEventListener('DOMContentLoaded', function() {
  // Attach initial button listeners
  FacetFiltersForm.attachMobileButtonListeners();
  
  // Handle sort radio button selection - close drawer and submit form
  document.addEventListener('change', function(e) {
    if (e.target.classList.contains('mobile-facets__radio') && 
        e.target.closest('#FacetsWrapperMobileSort')) {
      const sortDrawer = document.querySelector('.mobile-sort__disclosure');
      const form = e.target.closest('form');
      
      FacetFiltersForm.syncMobileSortSelection(e.target.value);

      // Update the mobile sort button text
      const selectedLabel = e.target.closest('label').querySelector('.mobile-facets__label-text');
      const sortButton = document.querySelector('[data-mobile-sort-trigger]');
      if (selectedLabel && sortButton) {
        const sortButtonText = sortButton.querySelector('span:first-child');
        if (sortButtonText) {
          sortButtonText.textContent = selectedLabel.textContent.trim();
        }
      }
      
      // Close the sort drawer
      if (sortDrawer) {
        const summary = sortDrawer.querySelector('summary');
        if (summary) {
          summary.click();
        }
      }
      
      // Submit the form to apply sorting
      if (form) {
        // Start from the current filters, search term and price range, so sorting doesn't drop them
        const searchParams = new URLSearchParams(FacetFiltersForm.searchParamsPrev || window.location.search.slice(1));
        new FormData(form).forEach((value, key) => {
          if (key !== 'sort_by' && !searchParams.has(key)) searchParams.set(key, value);
        });
        searchParams.set('sort_by', e.target.value);
        searchParams.delete('page');
        const facetForm = document.querySelector('facet-filters-form');
        if (facetForm && facetForm.onSubmitForm) {
          facetForm.onSubmitForm(searchParams.toString(), e);
        }
      }
    }
  });
  
  // Handle overlay click to close sort drawer
  document.addEventListener('click', function(e) {
    const sortDrawer = document.querySelector('.mobile-sort__disclosure');
    if (sortDrawer && sortDrawer.hasAttribute('open')) {
      const sortInner = sortDrawer.querySelector('.mobile-sort__inner');
      // Check if click is outside the sort drawer content and not on trigger buttons
      const isOnSortTrigger = e.target.closest('[data-mobile-sort-trigger]');
      const isOnFilterTrigger = e.target.closest('[data-mobile-filter-trigger]');
      if (sortInner && !sortInner.contains(e.target) && !isOnSortTrigger && !isOnFilterTrigger) {
        const summary = sortDrawer.querySelector('summary');
        if (summary) {
          summary.click();
        }
      }
    }
  });
});



